import { useState, useEffect } from 'react';
import { getPublicEventDetail, getPublicEventTicketTypes, getPublicEventAddons } from '../../services/eventService';
import { formatMoney } from '../../utils/money';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${m} ${ampm}`;
}

/**
 * Public event preview — shown to unauthenticated users who visit /event/:id
 * Displays event info and a "Register" button that triggers login.
 */
export default function PublicEventPreview({ eventId, onLogin }) {
  const [event, setEvent] = useState(null);
  // Currency-aware formatter using the event's stored currency code.
  const formatCents = (cents) => formatMoney(cents, event?.currency || 'usd');
  const [ticketTypes, setTicketTypes] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    loadEvent();
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadEvent() {
    setLoading(true);
    const data = await getPublicEventDetail(eventId);
    if (!data) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setEvent(data);

    const [tt, ao] = await Promise.all([
      getPublicEventTicketTypes(eventId),
      getPublicEventAddons(eventId),
    ]);
    setTicketTypes(tt);
    setAddons(ao);
    setLoading(false);
  }

  function handleRegisterClick() {
    setShowLogin(true);
  }

  function handleLoginSubmit(e) {
    e.preventDefault();
    if (email.trim()) {
      onLogin?.(email.trim());
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading event...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🎫</div>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
            Event Not Found
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: 24 }}>
            This event may have been removed or the link is incorrect.
          </p>
          <a
            href="/"
            style={{
              display: 'inline-block', padding: '10px 20px',
              background: 'var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)',
              textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem',
            }}
          >
            Go to Proper Miles
          </a>
        </div>
      </div>
    );
  }

  const hasTickets = ticketTypes.length > 0;
  const todayStr = new Date().toISOString().split('T')[0];
  const isPastEvent = event.event_date < todayStr;
  const isToday = event.event_date === todayStr
    || (event.end_date && event.event_date <= todayStr && event.end_date >= todayStr);

  return (
    <div style={{
      minHeight: '100dvh', background: 'var(--bg-primary)', width: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      {/* Banner */}
      {event.banner_url && (
        <div style={{ width: '100%', maxWidth: 540, maxHeight: 280, overflow: 'hidden', borderRadius: 'var(--radius-sm)' }}>
          <img
            src={event.banner_url}
            alt=""
            style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 540, padding: '24px 20px', boxSizing: 'border-box' }}>
        {/* Club logo — large & centered */}
        {event.club_logo_url && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <img
              src={event.club_logo_url}
              alt={event.club_name}
              style={{ maxHeight: 72, maxWidth: 240, objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Title */}
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 16, lineHeight: 1.2, textAlign: 'center' }}>
          {event.title}
        </h1>

        {/* Date & Time */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          padding: '16px', background: 'var(--bg-card)',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.25rem' }}>📅</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                {formatDate(event.event_date)}
                {event.end_date && event.end_date !== event.event_date && ` – ${formatDate(event.end_date)}`}
              </div>
              {event.event_time && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  {formatTime(event.event_time)}
                  {event.end_time && ` – ${formatTime(event.end_time)}`}
                </div>
              )}
            </div>
          </div>
          {event.location_text && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.25rem' }}>📍</span>
              <span style={{ fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                {event.location_text}
              </span>
            </div>
          )}
          {event.registration_type === 'car' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.25rem' }}>🚗</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Vehicle registration required
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <div style={{
            fontSize: '0.9375rem', color: 'var(--text-secondary)',
            lineHeight: 1.6, marginBottom: 24,
            whiteSpace: 'pre-wrap',
          }}>
            {event.description}
          </div>
        )}

        {/* Ticket Types */}
        {hasTickets && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
              Ticket Options
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ticketTypes.map(tt => {
                const soldOut = tt.quantity_available && tt.sold_count >= tt.quantity_available;
                return (
                  <div key={tt.id} style={{
                    padding: '12px 16px', background: 'var(--bg-card)',
                    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    opacity: soldOut ? 0.5 : 1,
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                        {tt.name}
                      </div>
                      {tt.description && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {tt.description}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {soldOut ? (
                        <span style={{
                          fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)',
                          background: 'rgba(211, 71, 74, 0.15)', padding: '3px 8px', borderRadius: 4,
                        }}>
                          Sold Out
                        </span>
                      ) : (
                        <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: tt.is_free ? '#22c55e' : 'var(--text-primary)' }}>
                          {tt.is_free ? 'Free' : formatCents(tt.price_cents)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Addons */}
        {addons.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
              Available Add-ons
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {addons.map(ao => (
                <div key={ao.id} style={{
                  padding: '10px 16px', background: 'var(--bg-card)',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      {ao.name}
                    </span>
                    {ao.description && (
                      <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginLeft: 8 }}>
                        — {ao.description}
                      </span>
                    )}
                  </div>
                  <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', flexShrink: 0 }}>
                    {formatCents(ao.price_cents)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Register / Login CTA */}
        {!isPastEvent && !showLogin && event.status !== 'cancelled' && (
          <button
            onClick={handleRegisterClick}
            style={{
              width: '100%', padding: '14px 24px',
              background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-sm)',
              fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
            }}
          >
            {isToday ? 'Sign In to Check In' : 'Register for This Event'}
          </button>
        )}

        {event.status === 'cancelled' && (
          <div style={{
            textAlign: 'center', padding: '14px 24px',
            background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444',
            borderRadius: 'var(--radius-sm)', fontWeight: 600,
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}>
            This event has been cancelled
          </div>
        )}

        {isPastEvent && event.status !== 'cancelled' && (
          <div style={{
            textAlign: 'center', padding: '14px 24px',
            background: 'rgba(200, 200, 200, 0.08)', color: 'var(--text-muted)',
            borderRadius: 'var(--radius-sm)', fontWeight: 600,
          }}>
            This event has ended
          </div>
        )}

        {/* Inline login form */}
        {showLogin && (
          <div style={{
            marginTop: 8, padding: '20px',
            background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
              Sign in to register
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              Enter your email or phone number and we'll send you a verification code.
            </p>
            <form onSubmit={handleLoginSubmit}>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email or phone number"
                required
                autoFocus
                style={{
                  width: '100%', padding: '12px 14px',
                  background: 'var(--bg-primary)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  fontSize: '1rem', marginBottom: 12, boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                style={{
                  width: '100%', padding: '12px 24px',
                  background: 'var(--accent)', color: '#fff',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer',
                }}
              >
                Continue
              </button>
            </form>
            <button
              onClick={() => setShowLogin(false)}
              style={{
                marginTop: 12, background: 'none', border: 'none',
                color: 'var(--text-muted)', fontSize: '0.8125rem', cursor: 'pointer',
                width: '100%', textAlign: 'center',
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Powered by */}
        <div style={{ textAlign: 'center', marginTop: 40, paddingBottom: 20 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Powered by{' '}
            <a href="https://garage.propermiles.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              Proper Miles
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
